import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import homeImage from '../../../assets/images/homeimg.webp';

const HeroSection = () => {
  // Stagger animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.95, x: 40 },
    visible: {
      opacity: 1,
      scale: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1],
        delay: 0.3,
      },
    },
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-amber-50/80 via-white to-orange-50/50">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Large soft circle */}
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        {/* Smaller accent */}
        <div className="absolute top-1/2 -left-20 w-64 h-64 bg-secondary/5 rounded-full blur-2xl" />
        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      {/* Main content */}
      <div className="relative min-h-[70vh] flex items-center py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">

            {/* Left column - Text content */}
            <motion.div
              className="order-2 lg:order-1 relative z-10"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Badge */}
              <motion.div variants={itemVariants} className="mb-6">
                <span className="inline-flex items-center gap-2 bg-primary/10 text-primary-dark px-4 py-2 rounded-full text-sm font-semibold tracking-wide">
                  <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                  Bienvenue au Groupe Scolaire Sanaa
                </span>
              </motion.div>

              {/* Heading */}
              <motion.h1
                variants={itemVariants}
                className="text-4xl sm:text-5xl lg:text-display font-bold text-gray-900 mb-6 leading-[1.1] tracking-tight"
              >
                Une éducation{' '}
                <span className="relative inline-block">
                  <span className="relative z-10 text-primary">d'excellence</span>
                  <svg
                    className="absolute -bottom-2 left-0 w-full h-3 text-primary/20"
                    viewBox="0 0 200 12"
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M0 8 Q50 0 100 8 T200 8"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="8"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>{' '}
                pour l'avenir de votre enfant
              </motion.h1>

              {/* Description */}
              <motion.p
                variants={itemVariants}
                className="text-lg text-gray-600 mb-8 max-w-xl leading-relaxed"
              >
                Notre établissement offre un enseignement de qualité de la maternelle au lycée,
                préparant les élèves à réussir dans un monde en constante évolution.
              </motion.p>

              {/* Stats row */}
              <motion.div
                variants={itemVariants}
                className="flex flex-wrap items-center gap-6 mb-8 pb-8 border-b border-gray-200"
              >
                <div>
                  <div className="text-3xl font-bold text-gray-900">28+</div>
                  <div className="text-sm text-gray-500 font-medium">Ans d'expérience</div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <span className="px-4 py-2 bg-primary/10 text-primary-dark text-sm font-semibold rounded-full">
                    Maternelle
                  </span>
                  <span className="px-4 py-2 bg-secondary/10 text-secondary text-sm font-semibold rounded-full">
                    Primaire
                  </span>
                  <span className="px-4 py-2 bg-accent/10 text-accent text-sm font-semibold rounded-full">
                    Collège
                  </span>
                  <span className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-semibold rounded-full">
                    Lycée
                  </span>
                </div>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div variants={itemVariants} className="flex flex-wrap gap-4">
                <Button to="/notre-ecole" variant="primary" size="large">
                  Découvrir notre école
                </Button>
                <Button to="/contact" variant="outline" size="large">
                  Nous contacter
                </Button>
              </motion.div>
            </motion.div>

            {/* Right column - Image card */}
            <motion.div
              className="order-1 lg:order-2 relative"
              variants={imageVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Decorative frame behind image */}
              <div className="absolute -inset-4 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-3xl transform rotate-2 pointer-events-none" />

              {/* Main image container */}
              <div className="relative">
                {/* Image card */}
                <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-gray-900/10 ring-1 ring-gray-900/5">
                  <img
                    src={homeImage}
                    alt="Élèves du Groupe Scolaire Sanaa lors de la cérémonie de remise des diplômes"
                    className="w-full h-auto object-cover aspect-[4/3]"
                  />

                  {/* Subtle overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/20 via-transparent to-transparent" />
                </div>

                {/* Floating accent card */}
                <motion.div
                  className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 border border-gray-100"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">Excellence académique</div>
                      <div className="text-xs text-gray-500">Depuis 1997</div>
                    </div>
                  </div>
                </motion.div>

                {/* Top right decorative element */}
                <motion.div
                  className="absolute -top-4 -right-4 w-20 h-20 bg-secondary rounded-full flex items-center justify-center shadow-lg"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1, duration: 0.4, type: 'spring' }}
                >
                  <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
                  </svg>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Curved bottom edge */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg
          viewBox="0 0 1440 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-auto"
          preserveAspectRatio="none"
        >
          <path
            d="M0 0L48 8.875C96 17.75 192 35.5 288 53.25C384 71 480 88.75 576 84.875C672 81 768 55.5 864 44.375C960 33.25 1056 36.5 1152 48.625C1248 60.75 1344 81.75 1392 92.25L1440 102.75V120H1392C1344 120 1248 120 1152 120C1056 120 960 120 864 120C768 120 672 120 576 120C480 120 384 120 288 120C192 120 96 120 48 120H0V0Z"
            fill="white"
          />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
